import type { InspireControlAction } from "../inspireControlMessages"
import type {
  GuestMeetingJoinResponse,
  MeetingInspirePanel,
  MeetingPollPanel,
  MeetingMessagesPanel,
  MeetingPanelEventPayload,
  MeetingQuestionsPanel,
  MeetingResponse,
} from "services/api/meetingsClient"

export type JoinInfo = {
  /** Local entry selector only; never room, admission or permission proof. */
  native_tools_expires_at?: string
  native_entry_snapshot?: import("mobile/nativeBridge").NativeCallState
  retained_native?: boolean
  retained_native_guest?: boolean

  native_calls_enabled?: boolean
  url: string
  token?: string | null
  /** Whether this join still requires explicit host admission, even with a Daily token. */
  requires_admission?: boolean
  waiting_room_enabled?: boolean
  waiting_room_unlocked?: boolean
  can_unlock_wait_room?: boolean
  /** A non-host pair participant may start the main room's transcription while the host is absent. */
  can_start_transcription?: boolean
  /** Where a mentor or mentee finds their For next time record after the call. */
  continuity?: ContinuityLink
  display_name?: string
  guest_session_id?: string
  breakout_token?: string | null
  questions_panel?: MeetingQuestionsPanel
  messages_panel?: MeetingMessagesPanel
  inspire_panel?: MeetingInspirePanel
  poll_panel?: MeetingPollPanel
}

/** Old join responses encoded admission by omitting the token; newer tokens can knock. */
export const requiresMeetingAdmission = (info: JoinInfo | null | undefined): boolean => (
  info?.requires_admission !== undefined
    ? Boolean(info.requires_admission)
    : Boolean(info?.waiting_room_enabled && !info.token)
)

export type LoadState = "loading" | "guest-choice" | "guest-form" | "joining" | "ready" | "denied" | "removed" | "access-ended" | "ended" | "left" | "error"

export type ContinuityLink = {
  role: "mentor" | "mentee"
  relationship_id?: string | null
  mentee_id?: number | string | null
  /** The meeting is this pair's own 1:1 (only the mentor and mentee are invited). */
  pair_meeting?: boolean
}

/** The mentorship page with the attendee's record opened, from the join payload's continuity link. */
export const continuityRecordPath = (link: ContinuityLink): string => {
  if (link.relationship_id) return `/mentorship?record=${encodeURIComponent(String(link.relationship_id))}`
  if (link.mentee_id) return `/mentorship?mentee=${encodeURIComponent(String(link.mentee_id))}`
  // A mentor leaving a group session: open the notes on their first current mentee.
  return "/mentorship?nexttime=1"
}

export type StoredGuestSession = {
  displayName: string
  guestSessionId: string
  breakoutToken?: string
}

export type DirectMessageToast = {
  id: string
  threadId: string
  recipientIdentityKey: string
  senderName: string
  body: string
}

export type MessageOpenTarget = Partial<Pick<DirectMessageToast, "threadId" | "recipientIdentityKey">>

export type ActiveMeeting = MeetingResponse | GuestMeetingJoinResponse["meeting"]

export type WaitRoomStatus = "idle" | "requesting" | "waiting" | "admitted"

/**
 * Which Daily client renders the call. Decided once per join from
 * `use_legacy_daily_call` (WiseFriend Integration (Beta)).
 */

export type CallSurfaceAttendee = {
  userId?: string | number
  isAuthenticated: boolean
  isGuest: boolean
  guestToken: string | null
  displayName: string
  guestSessionId: string | null
  breakoutToken?: string | null
}

export type CallSurfaceWaitRoom = {
  status: WaitRoomStatus
  unlocked: boolean
  unlocking: boolean
  unlockError: string
  canUnlock: boolean
  /** Unlock the waiting room through the WiseFriend API. Resolves true on success. */
  unlock: () => Promise<boolean>
}

export type CallSurfaceQuestions = {
  showPanel: boolean
  showReviewerPanel: boolean
  showGuestPanel: boolean
  showParticipantPanel: boolean
  canReview: boolean
}

export type CallSurfaceMessages = {
  enabled: boolean
  unreadCount: number
  /** Transport and expiry stay in the shell; the surface stacks both message previews. */
  notification?: {
    toast: DirectMessageToast
    onOpen: () => void
    onDismiss: () => void
  } | null
}

export type CallSurfaceErrorKind = "fatal" | "device"

/** Terminal cause established by the attendee action or an authoritative server result. */
export type CallLeaveReason = "left" | "removed" | "access-ended" | "ended" | "cancelled"

/**
 * Contract between the shell and the WiseFriend call surface.
 * The shell owns meeting/attendee lifecycle; surfaces own everything Daily-specific and
 * report shared lifecycle events through these callbacks.
 */
export type CallSurfaceProps = {
  toolsRecovery?: { blocked?: boolean; status: "loading" | "ready" | "degraded" | "denied"; retry: () => void }
  meetingId: string
  joinInfo: JoinInfo
  /** Keep panel credentials and admission metadata current after Daily recovery. */
  onJoinInfoRefresh?: (info: JoinInfo) => void
  meeting: MeetingResponse | null
  activeMeeting: ActiveMeeting | null
  attendee: CallSurfaceAttendee
  waitRoom: CallSurfaceWaitRoom
  questions: CallSurfaceQuestions
  messages: CallSurfaceMessages
  updateMeeting: (updater: (prev: MeetingResponse | null) => MeetingResponse | null) => void
  /** Subscribe to the meeting's realtime `meeting.*` events (lifecycle, breakout rooms) on the shell's socket. */
  subscribeMeetingEvents?: (listener: (event: MeetingPanelEventPayload) => void) => () => void
  onJoined: (localParticipantId: string) => void
  onWaiting: () => void
  onAccessGranted: (localParticipantId: string) => void
  onAccessDenied: () => void
  onParticipantCount: (count: number) => void
  onMessagesSurfaceOpenChange: (open: boolean) => void
  /** A direct message announced over Daily (the call's second transport beside the panel socket); same shape as the socket event. */
  onDirectMessageSignal?: (event: MeetingPanelEventPayload) => void
  onError: (message: string, kind?: CallSurfaceErrorKind) => void
  onLeft: (reason?: CallLeaveReason) => void
}

/**
 * Lets the Inspire panels render inside the custom surface instead of a routed iframe.
 * Presenter actions are confirmed server-side by the panel, then handed to the surface
 * which broadcasts them to other attendees over Daily app messages.
 */
export type InspireEmbedContext = {
  meetingId: string
  panelToken: string
  onControlAction: (action: InspireControlAction, activityId: string) => Promise<boolean>
  /** Increments whenever a presenter action arrives over Daily; the embedded panel reloads immediately. */
  refreshKey?: number
}

export type CallSurfaceHandle = {
  openMessages: (target?: MessageOpenTarget) => void
  isMessagesOpen: () => boolean | undefined
  getLocalParticipantId: () => string
  leave: () => void
}
